import * as GuidedTourActions from 'app/guided-tour/state/guided-tour.actions';
import * as moment from 'moment';

import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { PlanDocument, PlanMetadata } from 'fitforce-document-sync';
import { Observable, Subscription } from 'rxjs';

import { User } from 'app/classes/user';
import { DocumentService } from 'app/document/services/document.service';
import { GuidedTourSelectors } from 'app/guided-tour/state/guided-tour.selectors';
import { AuthService } from 'app/services/auth.service';
import { GuidedTourDataService } from 'app/services/guided-tour-data.service';
import { OfflineService } from 'app/services/offline.service';

@Component({
  selector: 'create-plan-document',
  templateUrl: './create-plan-document.component.html',
  styleUrls: ['./create-plan-document.component.scss'],
  animations: [
    trigger('fade', [
      state('void', style({ opacity: 0, transform: 'translateY(-100%)' })),
      transition('void <=> *', [animate('0.5s 0s ease-in-out')]),
    ]),
  ],
})
export class CreatePlanDocumentComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup;
  planUUID: string;
  loading = false;
  existingPlan: PlanMetadata;

  private currentUser: User;
  private currentUserSubscription: Subscription = new Subscription();
  private initSubscription: Subscription = new Subscription();
  private guidedTourStatusSub: Subscription = new Subscription();

  guidedTourToggledOn = false;

  @Select(GuidedTourSelectors.toggleStatus)guidedTourToggleStatus$: Observable<boolean>;

  constructor(
    private authService: AuthService,
    private documentService: DocumentService,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private offline: OfflineService,
    private guidedTourDataService: GuidedTourDataService,
    private store: Store
  ) {}

  ngOnInit() {
    this.form = this.formBuilder.group({
      name: this.formBuilder.control(null, [Validators.required]),
      startDate: this.formBuilder.control(null, [Validators.required]),
      numWeeks: this.formBuilder.control(null, [Validators.required, Validators.min(1)]),
      notes: this.formBuilder.control(null),
    });

    this.initSubscription = this.documentService.init().subscribe(() => {
      if (this.route.snapshot.params['id']) {
        this.planUUID = this.route.snapshot.params['id'];
        this.existingPlan = this.documentService.store.planMetadata.find(plan => plan.uuid === this.planUUID);
        if (this.existingPlan) {
          this.offline.getRecord<PlanDocument>(PlanDocument, this.planUUID).subscribe(document => {
            this.existingPlan.notes = document.document.notes;

            this.controls.name.setValue(this.existingPlan.name);
            this.controls.startDate.setValue(this.existingPlan.startDate.toDate());
            this.controls.numWeeks.setValue(this.existingPlan.numWeeks);
            this.controls.notes.setValue(this.existingPlan.notes || '');
          });
        }
      }
    });

    this.currentUserSubscription = this.authService.currentUserObs.subscribe(user => (this.currentUser = user));

  }

  ngAfterViewInit() {
    this.store.dispatch(new GuidedTourActions.GetGuidedTourToggleStatus());
    this.guidedTourStatusSub = this.guidedTourToggleStatus$.subscribe(status => {
      if (status !== undefined) {
        this.guidedTourToggledOn = status;
        if (this.guidedTourToggledOn) {
          setTimeout(() => this.guidedTourDataService.startGuidedTour('create-plan'), 1000);
        }
     }
    });
  }

  ngOnDestroy() {
    this.initSubscription.unsubscribe();
    this.currentUserSubscription.unsubscribe();
    if (this.guidedTourStatusSub) {
      this.guidedTourStatusSub.unsubscribe();
    }
  }

  get controls() {
    return {
      name: this.form.get('name'),
      startDate: this.form.get('startDate'),
      numWeeks: this.form.get('numWeeks'),
      notes: this.form.get('notes'),
    };
  }

  createPlan() {
    this.loading = true;
    const planMetadata = new PlanMetadata({
      uuid: this.planUUID || null,
    });
    planMetadata.name = this.controls.name.value;
    planMetadata.startDate = moment(this.controls.startDate.value);
    planMetadata.numWeeks = this.controls.numWeeks.value;
    planMetadata.notes = this.controls.notes.value;
    planMetadata.userUUID = this.currentUser.uuid;
    planMetadata.createdByUser = {
      email: this.currentUser.email,
      firstName: this.currentUser.firstName,
      lastName: this.currentUser.lastName,
      uuid: this.currentUser.uuid,
    };
    planMetadata.publishedToGroups = [];
    planMetadata.sharedWithUsers = [];

    let obs: Observable<void>;
    if (this.planUUID) {
      obs = this.documentService.updatePlan(planMetadata);
    } else {
      obs = this.documentService.createPlan(planMetadata);
    }

    obs.subscribe(() => {
      this.loading = false;
      this.router.navigate([`document/planning/${planMetadata.uuid}`]);
    });
  }
}
